// Shared protocols definitions; values and helpers are maintained here.
import { CATEGORICAL, STATUS, OTHER_COLOR } from './palette';


export type Protocol = "meshtastic" | "meshcore";


export const PROTOCOL_ORDER: Protocol[] = ["meshtastic", "meshcore"];


export const PROTOCOL_LABEL: Record<Protocol, string> = {
  meshtastic: "Meshtastic",
  meshcore: "MeshCore",
};


export function isProtocol(x: string | null | undefined): x is Protocol {
  return x === "meshtastic" || x === "meshcore";
}


// MeshCore neighbour-link evidence (2026-09-26 board item, "Node page:
// MeshCore neighbours") — shared between NodeDetailPage.tsx's own
// heard-by/hears tables and PositionHistoryMap.tsx's line/marker colours,
// one definition rather than two copies that could drift apart (this
// file's own stated purpose). `MESHCORE_SNR_COLOR_HEX` fixes ONE hex per
// colour regardless of theme, matching PositionHistoryMap's existing
// BIDIRECTIONAL_COLOR/ONE_WAY_COLOR convention — dark-mode values from
// CATEGORICAL.red/yellow/green above. The colour BOUNDARIES themselves
// are never hard-coded here — they come from
// the backend (audit/snr.py via snr_color_settings on the API response);
// this is only the red/yellow/green -> hex swatch mapping.
export const MESHCORE_SNR_COLOR_HEX: Record<"red" | "yellow" | "green", string> = {
  red: CATEGORICAL.red.dark,
  yellow: CATEGORICAL.yellow.dark,
  green: CATEGORICAL.green.dark,
};


export const MESHCORE_SOURCE_LABEL: Record<"first_party" | "corescope", string> = {
  first_party: "our radio",
  corescope: "CoreScope (external)",
};


// MeshCore-truncation task (2026-09-29): the companion link caps an
// RX_LOG_DATA capture at 173 bytes on the wire — a packet flagged
// `mc_possibly_truncated` (ingest/meshcore_truncation.py) may be missing
// its own payload tail. Once a matching CoreScope capture recovers the
// missing bytes (ingest/meshcore_recovery.py sets `mc_recovered_from`),
// the tag switches from a caution to a plain fact. Both are short tags,
// not sentences — same style as AUDIT_VERDICT_INFO's "Proven"/"Likely".
export const MESHCORE_TRUNCATION_TAG_INFO: Record<"possibly_truncated" | "recovered_corescope", { label: string; color: { light: string; dark: string } }> = {
  possibly_truncated: { label: "Possibly truncated", color: STATUS.warning },
  recovered_corescope: { label: "Recovered from CoreScope", color: STATUS.good },
};


export function meshcoreTruncationTag(
  protocolExtra: Record<string, unknown> | null | undefined
): (typeof MESHCORE_TRUNCATION_TAG_INFO)[keyof typeof MESHCORE_TRUNCATION_TAG_INFO] | null {
  if (!protocolExtra) return null;
  if (protocolExtra.mc_recovered_from) return MESHCORE_TRUNCATION_TAG_INFO.recovered_corescope;
  if (protocolExtra.mc_possibly_truncated) return MESHCORE_TRUNCATION_TAG_INFO.possibly_truncated;
  return null;
}


// A fixed "something is in flight right now" colour, deliberately distinct
// from every categorical/status hue below so a moving ray always reads as
// "in motion" regardless of the packet type that triggered it (spec §3).
export const IN_FLIGHT_COLOR = "#ff3df0";


// Dim factor applied to out-of-scope nodes when the protocol toggle has a
// single protocol selected: dim rather than hide, to keep spatial context.
// Applied as a multiplier on opacity/stroke-opacity.
// High enough to still register as "a marker is here" against a busy
// basemap at a glance — the whole point of dimming over hiding is spatial
// context, which a near-invisible marker doesn't actually provide.
export const OUT_OF_SCOPE_DIM = 0.3;


// Region-boundary marker outline (spec §10: "Nodes near the region's
// boundary called out with a distinct marker outline ... so it's obvious
// who's sitting right at the edge and might be a candidate for
// reassignment or cross-region relay duty"). A double-ring stroke in this
// colour, painted only while a region filter is active — deliberately
// distinct from IN_FLIGHT_COLOR and every protocol/role colour so it never
// gets read as "this node's category changed."
export const REGION_BOUNDARY_COLOR = { light: "#b8860b", dark: "#d9a441" };


// --- Marker shape: PROTOCOL, not role or capability ---------------------
// Requirement: someone looking at a mixed map must be able to tell a MeshCore
// repeater from a Meshtastic repeater without clicking. Chosen encoding —
// shape = protocol (the thing that needs to be legible without a click,
// since colour alone can't carry two independent identities at once),
// colour = role within that protocol, ring = freshness. This retires the
// earlier "sensor gets a triangle" idea: sensor is now just one more named
// role in each protocol's own list, distinguished by colour like any other
// role — shape is reserved entirely for protocol, not spent on capability.
export const PROTOCOL_MARKER_SHAPE: Record<Protocol, "circle" | "square"> = {
  meshtastic: "circle",
  meshcore: "square",
};


// Rendered glyph for the MeshCore "square" marker (a symbol-layer text glyph
// — no icon/sprite asset pipeline needed, consistent with the existing
// sensor-triangle approach this replaces).
export const MESHCORE_MARKER_GLYPH = "■";


// A node whose protocol the backend hasn't reported (see
// lib/nodeProtocol.ts — this is common today: the live backend doesn't
// populate /api/nodes' protocol field at all yet). Rendered as its own
// third, deliberately distinct marker style — a HOLLOW circle, never a
// solid fill in either protocol's colour, so it can't be mistaken for a
// real classified role: the unknown case should be honest and visible,
// rather than silently dimmed or silently shown as a known role.
export const UNKNOWN_PROTOCOL_LABEL = "Unknown protocol";

export const UNKNOWN_PROTOCOL_COLOR = { light: "#c3c2b7", dark: "#52514e" };


// --- Meshtastic node roles ------------------------------------------------
// Meshtastic's own device-role enum (config.DeviceConfig.Role). The 8 named
// roles below get their own fixed categorical slot; anything else (a rarer
// role like client_hidden/lost_and_found/tak_tracker/router_late, or a
// value we don't recognise) falls into "other" rather than being guessed
// at. Order below puts the roles expected to co-occur most often (client,
// repeater, router) into the palette's first three slots, which are the
// only ones validated as distinguishable under every possible pairing —
// see the dataviz reference's "all-pairs" note.
export type MeshtasticRole =
  | "client"
  | "repeater"
  | "router"
  | "sensor"
  | "tracker"
  | "tak"
  | "client_mute"
  | "router_client"
  | "client_base"
  | "router_late"
  | "other";


export const MESHTASTIC_ROLE_ORDER: MeshtasticRole[] = [
  "client",
  "repeater",
  "router",
  "sensor",
  "tracker",
  "tak",
  "client_mute",
  "router_client",
  "client_base",
  "router_late",
];


export const MESHTASTIC_ROLE_LABEL: Record<MeshtasticRole, string> = {
  client: "Client",
  repeater: "Repeater",
  router: "Router",
  sensor: "Sensor",
  tracker: "Tracker",
  tak: "TAK",
  client_mute: "Client (mute)",
  router_client: "Router + client",
  client_base: "Client (base)",
  router_late: "Router (late)",
  other: "Other role",
};




// Exported (not just used internally) so node-protocol inference (see
// lib/nodeProtocol.ts) can check "is this raw role name exclusive to
// Meshtastic" without a second, hand-maintained list.
export const MESHTASTIC_ROLE_ALIASES: Record<string, MeshtasticRole> = {
  client: "client",
  // The live backend sends "CLIENT_BASE" for a plain client role — found
  // verifying against it directly, 2026-09-20. Kept as its own distinct
  // role (not folded into "client") as of the map colour rework, 2026-09-20:
  // The role-category grouping treats Client_Base as a different
  // category from a plain Client — see ROLE_CATEGORY below.
  client_base: "client_base",
  repeater: "repeater",
  router: "router",
  sensor: "sensor",
  tracker: "tracker",
  tak: "tak",
  client_mute: "client_mute",
  clientmute: "client_mute",
  router_client: "router_client",
  routerclient: "router_client",
  client_hidden: "other",
  clienthidden: "other",
  lost_and_found: "other",
  lostandfound: "other",
  tak_tracker: "other",
  taktracker: "other",
  router_late: "router_late",
  routerlate: "router_late",
};


export function normalizeMeshtasticRole(raw: string | null | undefined): MeshtasticRole {
  if (!raw) return "other";
  return MESHTASTIC_ROLE_ALIASES[raw.toLowerCase().trim()] ?? "other";
}


// --- MeshCore node roles ---------------------------------------------------
// MeshCore's own role vocabulary — genuinely different concepts from
// Meshtastic's, not a re-skin of it (repeater and sensor are the only names
// that happen to also exist on the Meshtastic side, and each is its own
// distinct enum value/colour here, never shared).
export type MeshCoreRole = "repeater" | "companion" | "room_server" | "sensor" | "observer" | "other";


export const MESHCORE_ROLE_ORDER: MeshCoreRole[] = ["repeater", "companion", "room_server", "sensor", "observer"];


export const MESHCORE_ROLE_LABEL: Record<MeshCoreRole, string> = {
  repeater: "Repeater",
  companion: "Companion",
  room_server: "Room server",
  sensor: "Sensor",
  observer: "Observer",
  other: "Other role",
};




export const MESHCORE_ROLE_ALIASES: Record<string, MeshCoreRole> = {
  repeater: "repeater",
  companion: "companion",
  client: "companion", // MeshCore's own "client" concept is a companion radio, not Meshtastic's CLIENT role
  room_server: "room_server",
  roomserver: "room_server",
  room: "room_server",
  "room server": "room_server",
  sensor: "sensor",
  observer: "observer",
};


export function normalizeMeshCoreRole(raw: string | null | undefined): MeshCoreRole {
  if (!raw) return "other";
  return MESHCORE_ROLE_ALIASES[raw.toLowerCase().trim()] ?? "other";
}


// --- Meshtastic packet types (portnums) -----------------------------------
export type MeshtasticPacketCategory =
  | "text_message"
  | "position"
  | "telemetry"
  | "nodeinfo"
  | "traceroute"
  | "routing"
  | "admin"
  | "other";


export const MESHTASTIC_PACKET_ORDER: MeshtasticPacketCategory[] = [
  "text_message",
  "position",
  "telemetry",
  "nodeinfo",
  "traceroute",
  "routing",
  "admin",
];


export const MESHTASTIC_PACKET_LABEL: Record<MeshtasticPacketCategory, string> = {
  text_message: "Text message",
  position: "Position",
  telemetry: "Telemetry",
  nodeinfo: "Node info",
  traceroute: "Traceroute",
  routing: "Routing",
  admin: "Admin",
  other: "Other",
};


export const MESHTASTIC_PACKET_COLOR: Record<MeshtasticPacketCategory, { light: string; dark: string }> = {
  text_message: CATEGORICAL.blue,
  position: CATEGORICAL.orange,
  telemetry: CATEGORICAL.aqua,
  nodeinfo: CATEGORICAL.yellow,
  traceroute: CATEGORICAL.magenta,
  routing: CATEGORICAL.green,
  admin: CATEGORICAL.violet,
  other: OTHER_COLOR,
};


const MESHTASTIC_PACKET_ALIASES: Record<string, MeshtasticPacketCategory> = {
  text: "text_message",
  text_message: "text_message",
  message: "text_message",
  position: "position",
  telemetry: "telemetry",
  device_metrics: "telemetry",
  environment_metrics: "telemetry",
  nodeinfo: "nodeinfo",
  node_info: "nodeinfo",
  traceroute: "traceroute",
  trace: "traceroute",
  routing: "routing",
  admin: "admin",
  control: "admin",
};


export function normalizeMeshtasticPacket(raw: string | null | undefined): MeshtasticPacketCategory {
  if (!raw) return "other";
  // The live backend sends Meshtastic's real protobuf PortNum names
  // verbatim — "POSITION_APP", "TELEMETRY_APP", "TEXT_MESSAGE_APP", etc.
  // (found verifying against it directly, 2026-09-20: every alias below
  // was previously a short lowercase guess that never matched real data,
  // so every packet fell into "other"). Strip the trailing "_app" that
  // every real portnum name carries before alias lookup, rather than
  // hardcoding both forms of every entry.
  let key = raw.toLowerCase().trim();
  if (key.endsWith("_app")) key = key.slice(0, -4);
  return MESHTASTIC_PACKET_ALIASES[key] ?? "other";
}


// meshview's own packet contract (GET /api/meshview/packets) reports a raw
// numeric `portnum`, not the string PortNum name /api/packets sends — this
// is Meshtastic's own fixed protobuf enum (portnums.proto), not guessed, but
// only the values actually distinguishable in this app's existing category
// set are listed; anything else honestly falls through to "other" rather
// than a wrong guess. Used only to plug a MeshviewPacket into the existing
// packetCategoryInfo()/normalizeMeshtasticPacket() vocabulary above (see
// lib/meshview.ts's adapter) — never a second, parallel category system.
export const MESHTASTIC_PORTNUM_NAME: Record<number, string> = {
  1: "text_message",
  3: "position",
  4: "nodeinfo",
  5: "routing",
  6: "admin",
  8: "other", // WAYPOINT_APP — no dedicated category yet
  67: "telemetry",
  70: "traceroute",
};


export function meshtasticPortnumName(portnum: number | null | undefined): string {
  if (portnum == null) return "other";
  return MESHTASTIC_PORTNUM_NAME[portnum] ?? "other";
}


// --- meshview firehose drain priority (live map, 2026-09-21) --------------
// Trace routes and messages get priority because the route is fully known
// and visible. Measured over a couple hours of
// real ingest: position 29%, telemetry 25%, nodeinfo 24%, text_message 5%,
// traceroute 0.4% — 78% of all traffic is machine chatter. Traceroute gets
// top priority because it's the one case the map can honestly claim shows
// the real physical route (the backend serves its actual ordered hop path,
// not an inference — ROUTE_TREE's confidence tiers are moot for these,
// every edge is proven); text messages get the next tier as real human
// traffic. Priority governs ORDERING and SURVIVAL under the drain
// queue/concurrency cap — never exclusivity: chatter is what keeps the map
// dense (the map should read as very busy), so a high-priority packet jumps
// the queue and is protected from eviction first, but normal-priority
// traffic still flows and is never fully starved. A registry, not raw
// portnum checks scattered through the scheduler — AppData.tsx's queue and
// eviction logic call meshviewQueuePriorityForPortnum, never a portnum
// literal of their own.
export type MeshviewQueuePriority = "high" | "normal";


export const MESHVIEW_QUEUE_PRIORITY_BY_PACKET_CATEGORY: Record<MeshtasticPacketCategory, MeshviewQueuePriority> = {
  traceroute: "high",
  text_message: "high",
  position: "normal",
  telemetry: "normal",
  nodeinfo: "normal",
  routing: "normal",
  admin: "normal",
  other: "normal",
};


export function meshviewQueuePriorityForPortnum(portnum: number | null | undefined): MeshviewQueuePriority {
  const category = meshtasticPortnumName(portnum) as MeshtasticPacketCategory;
  return MESHVIEW_QUEUE_PRIORITY_BY_PACKET_CATEGORY[category] ?? "normal";
}


// --- MeshCore packet types -------------------------------------------------
export type MeshCorePacketCategory =
  | "channel_message"
  | "direct_message"
  | "advert"
  | "trace"
  | "path"
  | "request"
  | "response"
  | "ack"
  | "other";


export const MESHCORE_PACKET_ORDER: MeshCorePacketCategory[] = [
  "channel_message",
  "direct_message",
  "advert",
  "trace",
  "path",
  "request",
  "response",
  "ack",
];


export const MESHCORE_PACKET_LABEL: Record<MeshCorePacketCategory, string> = {
  channel_message: "Channel message",
  direct_message: "Direct message",
  advert: "Advert / identity",
  trace: "Trace",
  path: "Path",
  request: "Request",
  response: "Response",
  ack: "Ack",
  other: "Other",
};


export const MESHCORE_PACKET_COLOR: Record<MeshCorePacketCategory, { light: string; dark: string }> = {
  channel_message: CATEGORICAL.blue,
  direct_message: CATEGORICAL.orange,
  advert: CATEGORICAL.aqua,
  trace: CATEGORICAL.yellow,
  path: CATEGORICAL.magenta,
  request: CATEGORICAL.green,
  response: CATEGORICAL.violet,
  ack: CATEGORICAL.red,
  other: OTHER_COLOR,
};


const MESHCORE_PACKET_ALIASES: Record<string, MeshCorePacketCategory> = {
  channel_message: "channel_message",
  channel_text: "channel_message",
  channel: "channel_message",
  channel_msg: "channel_message",
  group_text: "channel_message",
  // A bare "text" with no direct/channel qualifier is ambiguous; default to
  // the ambient/common case per the live-map spec §8 (channel traffic is
  // common, direct is comparatively rare) rather than guessing wrong silently.
  text: "channel_message",
  direct_message: "direct_message",
  direct_text: "direct_message",
  dm: "direct_message",
  direct_msg: "direct_message",
  advert: "advert",
  identity: "advert",
  trace: "trace",
  path: "path",
  request: "request",
  response: "response",
  ack: "ack",
  acknowledge: "ack",
  control: "other",
  // The wire type names our own radio and CoreScope rows carry (airtime/meshcore.py).
  grp_txt: "channel_message",
  msg_chan: "channel_message",
  grp_data: "channel_message",
  txt_msg: "direct_message",
  text_msg: "direct_message",
  msg_priv: "direct_message",
  req: "request",
  anon_req: "request",
  multipart: "other",
};


export function normalizeMeshCorePacket(raw: string | null | undefined): MeshCorePacketCategory {
  if (!raw) return "other";
  return MESHCORE_PACKET_ALIASES[raw.toLowerCase().trim()] ?? "other";
}


// --- Generic protocol-scoped dispatch helpers -----------------------------
// Used anywhere the caller has a `protocol` string in hand (packets always
// carry one per the API contract) and just wants "the right label/colour
// for this raw value in this protocol", without re-deriving the dispatch
// logic at each call site (feed row, detail panel, map paint expression).

export interface PacketCategoryInfo {
  key: string;
  label: string;
  color: { light: string; dark: string };
}


export function packetCategoryInfo(protocol: string | null | undefined, rawType: string | null | undefined): PacketCategoryInfo {
  if (protocol === "meshcore") {
    const key = normalizeMeshCorePacket(rawType);
    return { key, label: MESHCORE_PACKET_LABEL[key], color: MESHCORE_PACKET_COLOR[key] };
  }
  // Meshtastic is the default when protocol is missing/unrecognised — most
  // of today's traffic is Meshtastic-only, and an unresolvable protocol
  // still needs *some* deterministic classification rather than a crash.
  const key = normalizeMeshtasticPacket(rawType);
  return { key, label: MESHTASTIC_PACKET_LABEL[key], color: MESHTASTIC_PACKET_COLOR[key] };
}


export interface RoleInfo {
  key: string;
  label: string;
  color: { light: string; dark: string };
}


export function roleInfo(protocol: Protocol, rawRole: string | null | undefined): RoleInfo {
  if (protocol === "meshcore") {
    const key = normalizeMeshCoreRole(rawRole);
    return { key, label: MESHCORE_ROLE_LABEL[key], color: ROLE_SHADE.meshcore[MESHCORE_ROLE_CATEGORY[key]] };
  }
  const key = normalizeMeshtasticRole(rawRole);
  return { key, label: MESHTASTIC_ROLE_LABEL[key], color: ROLE_SHADE.meshtastic[MESHTASTIC_ROLE_CATEGORY[key]] };
}


// --- Map marker colour scheme (redone for readability) -
// The map was unreadable at density: shape-by-protocol, ring-by-freshness,
// and per-role colours all stacked on the same small dots produced visual
// noise at every real cluster. Rebuilt from scratch,
// behaviourally studied against another mesh dashboard's live map (screenshots/timing only,
// no source read — same clean-room boundary as the rest of this map): small
// flat dots, colour is the ONLY channel, full stop. Two modes:
//   - ALL protocols shown -> colour = PROTOCOL. Nothing else varies.
//   - Exactly one protocol shown -> colour = ROLE CATEGORY (below), a small
//     fixed set of buckets every protocol's own roles map into.
// Shape, freshness rings, and per-role colour still exist elsewhere in the
// app (node list badges, node detail) — this section is specifically the
// map's own, deliberately narrower channel budget.

// Protocol-only colour (ALL mode). A plain map keyed by Protocol, not a
// two-branch conditional — adding a third protocol (Reticulum is expected
// later) is a data change here, nothing in the map/legend/toggle logic
// needs to know the protocol count. Unrecognised/undeclared protocol uses
// UNKNOWN_PROTOCOL_COLOR (already defined above), not a colour from this map.
export const PROTOCOL_COLOR: Record<Protocol, { light: string; dark: string }> = {
  meshtastic: CATEGORICAL.green,
  meshcore: CATEGORICAL.blue,
};


// Role categories. Five buckets every protocol's own roles are grouped into. In Role colour mode a node is coloured
// by its protocol's colour family (green for Meshtastic, blue for MeshCore) in the shade of its role category, so a
// glance tells both which protocol and which kind of node. The shade tables are in ROLE_SHADE below.
export type RoleCategory = "companion_client" | "base_room_late" | "router_repeater" | "sensor" | "other";


export const ROLE_CATEGORY_ORDER: RoleCategory[] = ["companion_client", "base_room_late", "router_repeater", "sensor", "other"];


// What each category is called under each protocol (legend text), listing the roles it groups.
export const ROLE_CATEGORY_LABEL: Record<Protocol, Record<RoleCategory, string>> = {
  meshtastic: {
    companion_client: "Client / Client (mute)",
    base_room_late: "Client (base) / Router (late)",
    router_repeater: "Router / Repeater / Router + client",
    sensor: "Sensor / Tracker / TAK",
    other: "Other role",
  },
  meshcore: {
    companion_client: "Companion",
    base_room_late: "Room server",
    router_repeater: "Repeater",
    sensor: "Sensor",
    other: "Observer / Other role",
  },
};


// Role shades. Meshtastic roles are shades of green, MeshCore roles shades of blue (the protocol colour families,
// PROTOCOL_COLOR). Each protocol has its own light-map and dark-map ladder: a shade has to stand off the tile under
// it (3:1 or better, measured on real tiles), and one set cannot do that on both a pale and a dark map, so each map
// has its own ladder. No shade is near black or near white: the repeater shade is the most saturated, clearest hue
// in its family and the others differ from it by lightness, hue and saturation. Within a ladder neighbours are at least CIEDE2000 12
// apart (tests/roleShades.test.mjs checks both).
export const ROLE_SHADE: Record<Protocol, Record<RoleCategory, { light: string; dark: string }>> = {
  meshtastic: {
    router_repeater: { light: "#019d39", dark: "#0fcb54" },
    companion_client: { light: "#065223", dark: "#118f45" },
    base_room_late: { light: "#557309", dark: "#81a136" },
    sensor: { light: "#168774", dark: "#40a293" },
    other: { light: "#565e54", dark: "#878e82" },
  },
  meshcore: {
    router_repeater: { light: "#5481fd", dark: "#7aa3ff" },
    companion_client: { light: "#004794", dark: "#457ec3" },
    base_room_late: { light: "#2181a5", dark: "#4ac0ee" },
    sensor: { light: "#6762b3", dark: "#b3a7ea" },
    other: { light: "#797f94", dark: "#8088a5" },
  },
};


// Per-protocol raw-role -> category mapping, as DATA (per the coordinator's
// direction: adding a protocol or a role later must be a data change, not a
// logic change). Each protocol keeps its own normalizer (already defined
// above) and this just maps that normalized role onto one of the four
// shared category buckets.
export const MESHTASTIC_ROLE_CATEGORY: Record<MeshtasticRole, RoleCategory> = {
  client: "companion_client",
  client_mute: "companion_client",
  client_base: "base_room_late",
  router_late: "base_room_late",
  router: "router_repeater",
  repeater: "router_repeater",
  router_client: "router_repeater",
  sensor: "sensor",
  tracker: "sensor",
  tak: "sensor",
  other: "other",
};


export const MESHCORE_ROLE_CATEGORY: Record<MeshCoreRole, RoleCategory> = {
  companion: "companion_client",
  room_server: "base_room_late",
  repeater: "router_repeater",
  sensor: "sensor",
  observer: "other",
  other: "other",
};


// Generic dispatcher — the one place that needs to know which protocol maps
// through which table. Adding a third protocol means adding one branch here
// and its own ROLE_CATEGORY table above; the category set, its colours, and
// the legend/map code that reads them never change.
export function roleCategoryFor(protocol: Protocol, rawRole: string | null | undefined): RoleCategory {
  if (protocol === "meshcore") return MESHCORE_ROLE_CATEGORY[normalizeMeshCoreRole(rawRole)];
  return MESHTASTIC_ROLE_CATEGORY[normalizeMeshtasticRole(rawRole)];
}


/** The shade a node of this protocol and role category gets in Role colour mode. */
export function roleShade(protocol: Protocol, category: RoleCategory): { light: string; dark: string } {
  return ROLE_SHADE[protocol][category];
}
